import type { PartnerDirectoryProps } from "@modes0/ui-components/client";

const projectPartnerDirectories: Record<string, PartnerDirectoryProps> = {
    sliderobes: {
        layout: "stack",
        eyebrow: "Project media partner",
        title: "Drone footage developed with Lenscast.",
        titleComponent: "h2",
        description:
            "Lenscast supported the Sliderobes website with professional drone footage that gives customers a clearer view of the store location before they visit.",
        ctaLabel: "Visit partner website",
        whyRecommendedLabel: "Why we recommend them",
        howWeWorkTogetherLabel: "How we worked together",
        highlightsLabel: "Drone media support",
        tagsLabel: "Specialist services",
        disclosure:
            "Lenscast contributed drone filming expertise and aerial footage to the Sliderobes website project.",
        defaultLinkTarget: "_blank",
        defaultLinkRel: "noopener noreferrer",
        colors: {
            background: "#f7fbff",
            surface: "#ffffff",
            surfaceSoft: "#eef6ff",
            text: "#172033",
            textSoft: "#334155",
            textMuted: "#64748b",
            accent: "#005dba",
            accentSoft: "rgba(0, 93, 186, 0.12)",
            action: "#005dba",
            actionDark: "#002863",
            actionSoft: "rgba(0, 93, 186, 0.1)",
            border: "rgba(0, 93, 186, 0.16)",
            borderStrong: "rgba(0, 93, 186, 0.28)",
            chipBackground: "rgba(0, 93, 186, 0.08)",
            logoBackground: "#e8f3ff",
            shadow: "0 18px 44px rgba(17, 32, 51, 0.09)",
            hoverShadow: "0 24px 56px rgba(17, 32, 51, 0.14)",
        },
        maxWidth: 1360,
        tileGap: 18,
        className: "grow-leads-project-partner-directory",
        ariaLabel: "Lenscast drone media contribution to the Sliderobes website",
        partners: [
            {
                id: "lenscast",
                name: "Lenscast",
                href: "https://lenscast.com.au/industrial-drone-photography-video-sydney/",
                category: "Industrial drone photography and video production",
                relationship: "Sliderobes drone media partner",
                location: "Sydney",
                logo: {
                    src: "/images/projects/mini-tiles/lenscast.jpg",
                    alt: "Lenscast drone operator providing industrial drone photography in Sydney",
                    width: 218,
                    height: 218,
                    loading: "lazy",
                    decoding: "async",
                    objectFit: "cover",
                    objectPosition: "center",
                },
                description:
                    "Lenscast is a Sydney industrial media production team creating drone photography, aerial video, worksite photography and video for factories, warehouses, construction projects and commercial sites.",
                whyRecommended:
                    "Lenscast understood that the footage needed to do more than look impressive. Their aerial planning and filming expertise helped make the Sliderobes store location easier for customers to recognise before visiting.",
                howWeWorkTogether:
                    "For Sliderobes, Lenscast assisted with drone footage for the store information presented online. Grow Leads integrated the aerial media into the website so customers could understand the location more clearly.",
                highlights: [
                    {
                        id: "lenscast-commercial-drone-footage",
                        text: "Commercial drone footage planned around the Sliderobes store and the information customers needed before visiting.",
                    },
                    {
                        id: "lenscast-store-location-media",
                        text: "Clearer store information supported by wide aerial views that helped customers recognise the location before visiting.",
                    },
                    {
                        id: "lenscast-website-aerial-media",
                        text: "Website-ready aerial media prepared for location information, website content and digital marketing.",
                    },
                ],
                tags: [
                    {
                        id: "lenscast-industrial-drone-photography-sydney",
                        label: "Industrial drone photography Sydney",
                        backgroundColor: "rgba(0, 93, 186, 0.1)",
                        textColor: "#003f7f",
                    },
                    {
                        id: "lenscast-drone-videography-sydney",
                        label: "Drone videography Sydney",
                        backgroundColor: "rgba(0, 40, 99, 0.1)",
                        textColor: "#002863",
                    },
                    {
                        id: "lenscast-commercial-aerial-footage",
                        label: "Commercial aerial footage",
                        backgroundColor: "rgba(0, 93, 186, 0.1)",
                        textColor: "#003f7f",
                    },
                ],
                linkLabel: "Lenscast drone media Sydney",
                ariaLabel: "Visit Lenscast for industrial drone photography and video in Sydney",
                title: "Lenscast — industrial drone photography and video Sydney",
                schemaType: "ProfessionalService",
                accentColor: "#005dba",
                accentSoftColor: "rgba(0, 93, 186, 0.12)",
                logoBackgroundColor: "#e8f3ff",
            },
        ],
    },
    "jasmin-1-bankstown": {
        layout: "stack",
        eyebrow: "Project partner",
        title: "Food images developed with Big Bite Media.",
        titleComponent: "h2",
        description:
            "Big Bite Media supported the Jasmin 1 Bankstown website with food photography and hospitality media created for its menu, website pages and online marketing.",
        ctaLabel: "Visit partner website",
        whyRecommendedLabel: "Why we recommend them",
        howWeWorkTogetherLabel: "How we worked together",
        highlightsLabel: "Image and media support",
        tagsLabel: "Specialist services",
        disclosure:
            "Big Bite Media contributed food images and hospitality media to the Jasmin 1 Bankstown website project.",
        defaultLinkTarget: "_blank",
        defaultLinkRel: "noopener noreferrer",
        colors: {
            background: "#f3f8f3",
            surface: "#ffffff",
            surfaceSoft: "#eaf4eb",
            text: "#17251a",
            textSoft: "#3f5144",
            textMuted: "#64736a",
            accent: "#237a27",
            accentSoft: "rgba(35, 122, 39, 0.12)",
            action: "#237a27",
            actionDark: "#17551b",
            actionSoft: "rgba(35, 122, 39, 0.1)",
            border: "rgba(35, 122, 39, 0.16)",
            borderStrong: "rgba(35, 122, 39, 0.28)",
            chipBackground: "#e8f3e9",
            logoBackground: "#f4f4f4",
            shadow: "0 18px 44px rgba(23, 37, 26, 0.08)",
            hoverShadow: "0 24px 56px rgba(23, 37, 26, 0.12)",
        },
        maxWidth: 1360,
        tileGap: 18,
        className: "grow-leads-project-partner-directory",
        ariaLabel: "Big Bite Media partner contribution to the Jasmin 1 Bankstown website",
        partners: [
            {
                id: "big-bite-media",
                name: "Big Bite Media",
                href: "https://bigbitemedia.com.au",
                category: "Food photography and hospitality marketing",
                relationship: "Jasmin 1 image development partner",
                location: "Sydney",
                logo: {
                    src: "/images/projects/mini-tiles/bigbite.jpg",
                    alt: "Big Bite Media Sydney food photography for restaurant websites",
                    width: 218,
                    height: 218,
                    loading: "lazy",
                    decoding: "async",
                    objectFit: "cover",
                    objectPosition: "center",
                },
                description:
                    "Big Bite Media is a Sydney food photography and hospitality marketing specialist creating restaurant photography, food videography and digital content for websites, menus, advertising and social media.",
                whyRecommended:
                    "Their team understands how restaurant images need to work across menus, websites and marketing, with clear food styling and useful crops for different digital formats.",
                howWeWorkTogether:
                    "For Jasmin 1 Bankstown, Big Bite Media helped develop the food images used across the new website. Grow Leads integrated the media into the menu, service pages and customer journey.",
                highlights: [
                    {
                        id: "big-bite-website-food-photography",
                        title: "Website food photography",
                        text: "Restaurant images prepared for menu pages, feature sections and mobile browsing.",
                    },
                    {
                        id: "big-bite-hospitality-image-development",
                        title: "Hospitality image development",
                        text: "Food and venue media shaped around the Jasmin 1 brand and Lebanese menu.",
                    },
                    {
                        id: "big-bite-restaurant-marketing-content",
                        title: "Restaurant marketing content",
                        text: "Reusable visual content for the website, digital campaigns and social media.",
                    },
                ],
                tags: [
                    {
                        id: "big-bite-food-photography-sydney",
                        label: "Food photography Sydney",
                        backgroundColor: "#e8f3e9",
                        textColor: "#1b651f",
                    },
                    {
                        id: "big-bite-restaurant-photography",
                        label: "Restaurant photography",
                        backgroundColor: "#deeddf",
                        textColor: "#17551b",
                    },
                    {
                        id: "big-bite-hospitality-marketing",
                        label: "Hospitality marketing",
                        backgroundColor: "#eef6ef",
                        textColor: "#286c2c",
                    },
                ],
                linkLabel: "Explore Big Bite Media food photography",
                ariaLabel: "Visit Big Bite Media for Sydney food photography and hospitality marketing",
                title: "Big Bite Media — Sydney food photography and hospitality marketing",
                schemaType: "ProfessionalService",
                accentColor: "#237a27",
                accentSoftColor: "rgba(35, 122, 39, 0.12)",
                logoBackgroundColor: "#f4f4f4",
            },
        ],
    },
};

export const getProjectPartnerDirectoryBySlug = (slug: string) => projectPartnerDirectories[slug];
